import type { I18n } from 'vue-i18n';

// 支持的语言列表
export const SUPPORTED_LANGUAGES = ['sc', 'jp'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// 格式化语言代码
export const formatLang = (lang: string | null | undefined): string => {
  if (!lang) return '';
  return lang.replace('_', '-').split('-')[0]?.toLowerCase() || '';
};

// 解析 User Agent 获取语言信息
export const parseUA = () => {
  const ua = navigator.userAgent;
  // 这里可以根据实际需求解析UA中的语言信息
  return {
    lang: (ua.match(/\[Lang\/([^\]]+)\]/i)?.[1] || '').replace('_', '-'),
    isWebview: /(YourAppName|WebView)/i.test(ua)
  }
};

// 验证语言是否支持
export const validateLang = (lang?: string): lang is SupportedLanguage => {
  if (!lang) return false;
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
};

// 检测当前语言
export async function detectLanguage(): Promise<SupportedLanguage> {
  const sources = [
    // parseUA作为第一个检测项
    () => parseUA().lang,
    () => new URLSearchParams(location.search).get('lang'),
    () => window.clientBridge?.lang,
    () => {
      const envLang = import.meta.env.VITE_APP_ACCEPT_LANGUAGE;
      return envLang || null;
    },
    () => localStorage.getItem('user_lang'),
    () => navigator.language?.split('-')[0]
  ];

  for (const source of sources) {
    try {
      const lang = await source();
      const formattedLang = formatLang(lang);
      if (validateLang(formattedLang)) {
        console.log('语言检测结果:', formattedLang);
        return formattedLang;
      }
    } catch (error) {
      console.warn('语言检测源执行失败:', error);
      continue;
    }
  }
  
  return 'sc'; // 默认返回简体中文
}

// 设置语言
export const setLanguage = async (i18n: I18n, lang?: string) => {
  const targetLang = lang || await detectLanguage();
  
  if (validateLang(targetLang)) {
    try {
      // 动态加载语言包
      const messages = await import(`../locales/${targetLang}.ts`);
      i18n.global.setLocaleMessage(targetLang, messages.default);
      
      // 设置当前语言
      if (typeof i18n.global.locale === 'object') {
        i18n.global.locale.value = targetLang;
      } else {
        // @ts-ignore - 某些版本的 vue-i18n 类型定义可能不完整
        i18n.global.locale = targetLang;
      }
      
      // 保存到本地存储
      localStorage.setItem('user_lang', targetLang);
      
      // 设置 HTML lang 属性
      document.documentElement.setAttribute('lang', targetLang);
      
      console.log('语言设置成功:', targetLang);
    } catch (error) {
      console.error('设置语言失败:', error);
    }
  } else {
    console.warn('不支持的语言:', targetLang);
  }
}; 